import { supabase } from '../lib/supabase';

const newsletterService = {
  /**
   * Get all newsletters (for admin dashboard)
   */
  async getAll() {
    const { data, error } = await supabase?.from('newsletters')?.select('*')?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(newsletter => ({
      id: newsletter?.id,
      title: newsletter?.title,
      excerpt: newsletter?.summary,
      content: newsletter?.content,
      status: newsletter?.status,
      publishedAt: newsletter?.published_at,
      createdAt: newsletter?.created_at,
      updatedAt: newsletter?.updated_at,
      slug: newsletter?.slug,
      tags: newsletter?.tags || []
    })) || [];
  },

  /**
   * Get published newsletters only (for public pages)
   */
  async getPublished() {
    const { data, error } = await supabase?.from('newsletters')?.select('*')?.eq('status', 'published')?.order('published_at', { ascending: false });

    if (error) throw error;

    return data?.map(newsletter => ({
      id: newsletter?.id,
      title: newsletter?.title,
      excerpt: newsletter?.summary,
      content: newsletter?.content,
      publishedAt: newsletter?.published_at,
      slug: newsletter?.slug,
      tags: newsletter?.tags || []
    })) || [];
  },

  /**
   * Get single newsletter by ID
   */
  async getById(id) {
    const { data, error } = await supabase?.from('newsletters')?.select('*')?.eq('id', id)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      title: data?.title,
      excerpt: data?.summary,
      content: data?.content,
      status: data?.status,
      publishedAt: data?.published_at,
      createdAt: data?.created_at,
      slug: data?.slug,
      tags: data?.tags || []
    };
  },

  /**
   * Get single newsletter by slug
   */
  async getBySlug(slug) {
    const { data, error } = await supabase?.from('newsletters')?.select('*')?.eq('slug', slug)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      title: data?.title,
      excerpt: data?.summary,
      content: data?.content,
      status: data?.status,
      publishedAt: data?.published_at,
      createdAt: data?.created_at,
      slug: data?.slug,
      tags: data?.tags || []
    };
  },

  /**
   * Create new newsletter
   */
  async create(newsletterData) {
    // Convert camelCase to snake_case for database
    const { data, error } = await supabase?.from('newsletters')?.insert({
        title: newsletterData?.title,
        summary: newsletterData?.excerpt,
        content: newsletterData?.content,
        status: newsletterData?.status || 'draft',
        published_at: newsletterData?.status === 'published' ? new Date()?.toISOString() : null,
        slug: newsletterData?.slug,
        tags: newsletterData?.tags || []
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      title: data?.title,
      excerpt: data?.summary,
      content: data?.content,
      status: data?.status,
      publishedAt: data?.published_at,
      slug: data?.slug,
      tags: data?.tags || []
    };
  },

  /**
   * Update existing newsletter
   */
  async update(id, newsletterData) {
    console.log('[newsletterService.update] Called with ID:', id);
    console.log('[newsletterService.update] Newsletter data:', newsletterData);
    
    if (!id) {
      console.error('[newsletterService.update] No ID provided!');
      throw new Error('Newsletter ID is required for update');
    }

    const updateData = {
      title: newsletterData?.title,
      summary: newsletterData?.excerpt,
      content: newsletterData?.content,
      status: newsletterData?.status,
      slug: newsletterData?.slug,
      tags: newsletterData?.tags || []
    };

    // Set published_at when status changes to published
    if (newsletterData?.status === 'published') {
      updateData.published_at = new Date()?.toISOString();
    }

    console.log('[newsletterService.update] Update data prepared:', updateData);
    console.log('[newsletterService.update] Executing update with ID:', id);

    const { data, error } = await supabase?.from('newsletters')?.update(updateData)?.eq('id', id)?.select()?.single();

    if (error) {
      console.error('[newsletterService.update] Supabase error:', error);
      throw error;
    }

    console.log('[newsletterService.update] Update successful:', data);

    return {
      id: data?.id,
      title: data?.title,
      excerpt: data?.summary,
      content: data?.content,
      status: data?.status,
      publishedAt: data?.published_at,
      slug: data?.slug,
      tags: data?.tags || []
    };
  },

  /**
   * Delete newsletter
   */
  async delete(id) {
    const { error } = await supabase?.from('newsletters')?.delete()?.eq('id', id);

    if (error) throw error;
    return true;
  },

  /**
   * Publish newsletter (update status to published)
   */
  async publish(id) {
    const { data, error } = await supabase?.from('newsletters')?.update({ 
        status: 'published',
        published_at: new Date()?.toISOString()
      })?.eq('id', id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      title: data?.title,
      excerpt: data?.summary,
      content: data?.content,
      status: data?.status,
      publishedAt: data?.published_at,
      slug: data?.slug,
      tags: data?.tags || []
    };
  },

  /**
   * Check if slug is unique
   */
  async isSlugUnique(slug, excludeId = null) {
    let query = supabase?.from('newsletters')?.select('id')?.eq('slug', slug);

    if (excludeId) {
      query = query?.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.length === 0;
  }
};

export default newsletterService;